/**
 * Slim task slide-over for the supplier Project Hub.
 *
 * Suppliers only get whitelisted edits (progress + status, routed through the
 * `supplierUpdateProductionTask` callable via CRMContext.updateProjectTask);
 * mirrored onboarding checklist rows are read-only. The full staff
 * TaskDetailPanel stays internal — it is deeply coupled to staff-only data
 * (assignees, files, reminders, time tracking).
 */
import React, { useEffect, useState } from 'react';
import { Calendar, Loader2, MessageSquare, X } from 'lucide-react';
import type { ProjectTask, TaskStatus } from '../../types';
import './SupplierTaskPanel.css';

const STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'on-hold', label: 'On Hold' },
];

interface SupplierTaskPanelProps {
  task: ProjectTask;
  groupName?: string;
  projectLabel?: string;
  /** Mirrored onboarding checklist rows cannot be edited by suppliers. */
  readOnly?: boolean;
  onUpdate: (id: string, updates: Partial<ProjectTask>) => void | Promise<void>;
  onOpenProductionComments?: () => void;
  onClose: () => void;
}

const formatDate = (d: Date | undefined): string => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const SupplierTaskPanel: React.FC<SupplierTaskPanelProps> = ({
  task,
  groupName,
  projectLabel,
  readOnly,
  onUpdate,
  onOpenProductionComments,
  onClose,
}) => {
  const [progressDraft, setProgressDraft] = useState<number>(task.progress ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setProgressDraft(task.progress ?? 0);
    setError(null);
  }, [task.id, task.progress]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commit = async (updates: Partial<ProjectTask>) => {
    if (!task.id || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      await onUpdate(task.id, updates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const commitProgress = () => {
    if (progressDraft === (task.progress ?? 0)) return;
    void commit({ progress: progressDraft });
  };

  return (
    <div className="pm-detail-overlay" onClick={onClose}>
      <div className="pm-detail-panel pm-scroll pm-supplier-task-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pm-supplier-task-header">
          <div className="pm-supplier-task-header-text">
            <h3 className="pm-supplier-task-title">{task.title}</h3>
            <div className="pm-supplier-task-meta">
              {projectLabel && <span className="pm-supplier-task-project">{projectLabel}</span>}
              {groupName && <span className="pm-supplier-task-group">{groupName}</span>}
            </div>
          </div>
          <button
            type="button"
            className="pm-supplier-task-close"
            onClick={onClose}
            aria-label="Close task panel"
          >
            <X size={16} />
          </button>
        </div>

        <div className="pm-supplier-task-dates">
          <Calendar size={14} aria-hidden />
          <span>
            {formatDate(task.startDate)} – {formatDate(task.endDate)}
          </span>
        </div>

        {readOnly ? (
          <div className="pm-supplier-task-readonly" role="status">
            This row mirrors the project checklist and is read-only. Reach out through Production
            comments if something needs to change.
          </div>
        ) : (
          <>
            <label className="pm-supplier-task-field">
              <span className="pm-supplier-task-label">Status</span>
              <select
                className="pm-supplier-task-status"
                value={task.status}
                disabled={saving}
                onChange={(e) => void commit({ status: e.target.value as TaskStatus })}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="pm-supplier-task-field">
              <span className="pm-supplier-task-label">
                Progress <strong>{progressDraft}%</strong>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={progressDraft}
                disabled={saving}
                onChange={(e) => setProgressDraft(Number(e.target.value))}
                onMouseUp={commitProgress}
                onTouchEnd={commitProgress}
                onKeyUp={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    commitProgress();
                  }
                }}
                aria-label="Task progress"
              />
            </div>
          </>
        )}

        {saving && (
          <div className="pm-supplier-task-saving" role="status">
            <Loader2 size={13} className="pm-supplier-task-spin" aria-hidden /> Saving…
          </div>
        )}
        {error && (
          <div className="pm-supplier-task-error" role="alert">
            {error}
          </div>
        )}

        {onOpenProductionComments && (
          <button type="button" className="pm-supplier-task-comments-btn" onClick={onOpenProductionComments}>
            <MessageSquare size={14} aria-hidden /> Production comments
          </button>
        )}
      </div>
    </div>
  );
};

export default SupplierTaskPanel;
